// Finds a menu drink's liquor-slot recipe component, so the ticket UI can
// show a trustworthy default pour amount in the liquor sheet and can
// substitute the bartender's actual selection into the line sent to
// POST .../orders/:orderId/lines.
//
// ALPHA-07: this module used to re-derive a drink's liquor-slot ingredient
// from a duplicated client-side keyword list (LIQUOR_SLOT_KEYWORD_PATTERNS,
// kept in sync by hand with fixtures/real-menu.seed.ts's own SPIRIT_
// PATTERNS), flagged as an architecture gap in docs/review-record.md's
// Review 5 ("liquor-slot ingredient matching is duplicated client-side
// pending a server-side slot marker field"). That field now exists:
// menu_drink_components.is_liquor_slot (db/schema.ts), set at insert time
// by both seeds and backfilled for rows that existed before the column did
// (db/migrations/0006_soft_shriek.sql). This module now does one thing:
// find the (at most one) component the server already marked, and validate
// its unit is a pour amount the pricing engine can price.
//
// Review 5 also flagged a second, narrower gap this module used to paper
// over: a component the old keyword matcher DID find but whose unit could
// not be parsed as ounces silently priced an estimated 1.5 oz pour instead
// of blocking. There is no estimated pricing anywhere in this codebase's
// money path (src/domain/pricing.ts), so that estimate was always a latent
// wrong-price bug waiting for a misconfigured recipe to trigger it. Both
// gaps close the same way here: findLiquorSlotComponent returns undefined
// whenever it cannot produce a component AND a valid ounces value together,
// whether the slot component is simply missing or present with an
// unparseable unit. The caller (main.ts) treats an undefined result as a
// block: Add to Ticket stays disabled and the sheet shows "This drink
// needs a recipe update before it can be rung," never a guessed price.

import type { MenuDrinkComponentDto } from "./api.ts";

/** Normalizes any numeric-ish value (a DB-sourced decimal string with more
 * than two decimal places, e.g. "1.5000" from a NUMERIC(12,4) column, or a
 * number) into the "at most two decimal places" string
 * src/domain/pricing.ts's OUNCES_PATTERN requires. toFixed(2) always
 * yields exactly two decimal places, which satisfies "at most two" without
 * needing to strip trailing zeros. */
export function normalizeOunces(value: string | number): string {
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) {
    throw new RangeError(`cannot normalize "${String(value)}" as an ounces amount`);
  }
  return numeric.toFixed(2);
}

export interface LiquorSlotMatch {
  component: MenuDrinkComponentDto;
  ounces: string;
}

function parseOunces(component: MenuDrinkComponentDto): string | undefined {
  if (component.unit.toLowerCase() !== "oz") {
    return undefined;
  }
  const numeric = Number(component.quantity);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return undefined;
  }
  return normalizeOunces(numeric);
}

/**
 * Finds the drink's server-marked liquor-slot component (is_liquor_slot:
 * true) and its ounces. Returns undefined - the caller's signal to block
 * Add to Ticket with "needs a recipe update" - when either no component is
 * marked (a menu drink with requires_liquor_selection: true should always
 * have exactly one, per db/schema.ts's one-liquor-slot-per-drink partial
 * unique index; this guards a misconfigured recipe, not an expected path)
 * or the marked component's unit cannot be parsed as a pour amount. Never
 * estimates: a component IS returned only together with a real,
 * server-sourced ounces value the pricing engine can price exactly.
 */
export function findLiquorSlotComponent(
  components: readonly MenuDrinkComponentDto[],
): LiquorSlotMatch | undefined {
  const slotComponent = components.find((component) => component.is_liquor_slot);
  if (slotComponent === undefined) {
    return undefined;
  }
  const ounces = parseOunces(slotComponent);
  if (ounces === undefined) {
    return undefined;
  }
  return { component: slotComponent, ounces };
}
